import React, { useState } from "react";
import { usePopper } from "react-popper";
import { useTranslation } from "react-i18next";

import useVisible from "@hooks/useVisible";
import ClickAwayListener from "@components/common/ClickAwayListener/ClickAwayListener";
import Group from "@components/arrangement/Group/Group";
import Input from "@components/dataEntry/Input/Input";
import Button from "@components/dataEntry/Buttons/Button/Button";

import { EditorImageProps, EditorUtilsProps } from "../../TextEditor.types";
import ImageIcon from "../../icons/ImageIcon";
import "./CustomUtils.scss";

const initialStateImage = (): EditorImageProps => ({ url: "", alt: "" });

const AddImage = ({ editor }: EditorUtilsProps) => {
  const [image, setImage] = useState<EditorImageProps>(initialStateImage());
  const [buttonUtilsRef, setButtonUtilRef] = useState<HTMLElement | null>(null);
  const [popperRef, setPopperRef] = useState<HTMLElement | null>(null);

  const { isVisible, visibilityToggle, setVisibilityFalse } = useVisible(false);

  const { t: commonT } = useTranslation("common");

  const { styles, attributes } = usePopper(buttonUtilsRef, popperRef, {
    placement: "bottom",
    strategy: "fixed",
    modifiers: [
      {
        name: "offset",
        enabled: true,
        options: {
          offset: [0, 8]
        }
      }
    ]
  });

  const handleChangeImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImage((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  };

  const handleAddImage = () => {
    if (!image.url) return;

    if (image) {
      editor
        .chain()
        .focus()
        .setImage({ src: image.url, alt: image.alt || "event image" })
        .run();
      setVisibilityFalse();
      setImage(initialStateImage());
    }
  };

  return (
    <div className="text-editor-custom-utils">
      <button
        ref={setButtonUtilRef}
        title={commonT("addImage.button")}
        onClick={visibilityToggle}
        className="menu-utils-button"
      >
        <ImageIcon />
      </button>

      {isVisible && (
        <ClickAwayListener onClickAway={setVisibilityFalse}>
          <div
            ref={setPopperRef}
            className="text-editor-custom-utils-modal"
            style={{ ...styles.popper, zIndex: 100 }}
            {...attributes.popper}
          >
            <h3>{commonT("addImage.modal.title")}</h3>

            <Input
              name="url"
              label={commonT("addImage.modal.inputs.url.label")}
              placeholder={commonT("addImage.modal.inputs.url.placeholder")}
              value={image.url}
              onChange={handleChangeImage}
              required
            />
            <Group justifyContent="flex-end" fullWidth>
              <Button onClick={handleAddImage}>{commonT("save")}</Button>
            </Group>
          </div>
        </ClickAwayListener>
      )}
    </div>
  );
};

export default AddImage;
